<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import DenominatorEntryForm from '@/views/quality/infection-control/denominator/DenominatorEntryForm.vue'

definePage({
  meta: {
    subject: 'quality.infection_control.menu',
    action: 'denominator_entry',
  },
})

const unitId = cookieRef('selectedUnit')
const today = new Date()

// Month & Year filter
const monthOptions = [
  { title: 'January', value: 1 },
  { title: 'February', value: 2 },
  { title: 'March', value: 3 },
  { title: 'April', value: 4 },
  { title: 'May', value: 5 },
  { title: 'June', value: 6 },
  { title: 'July', value: 7 },
  { title: 'August', value: 8 },
  { title: 'September', value: 9 },
  { title: 'October', value: 10 },
  { title: 'November', value: 11 },
  { title: 'December', value: 12 },
]

const currentYearNum = today.getFullYear()

const yearOptions = Array.from({ length: 5 }, (_, i) => ({
  title: String(currentYearNum - i),
  value: currentYearNum - i,
}))

const selectedMonth = ref(today.getMonth() + 1)
const selectedYear = ref(currentYearNum)

// Location filter
const selectedLocation = ref(null)
const searchLocation = ref('')
const debouncedSearchLocation = refDebounced(searchLocation, 500)

const { data: locationData, isFetching: fetchingLocations, execute: fetchLocations } = useApi(createUrl('/masters/locations', {
  query: {
    limit: 20,
    q: debouncedSearchLocation,
    search: {
      unit_id: unitId,
      is_ic_enabled: 1,
    },
  },
}), {
  beforeFetch: ({ cancel }) => {
    if (!unitId.value) cancel()
  },
  immediate: false,
})

watch(() => unitId.value, () => {
  selectedLocation.value = null
  searchLocation.value = ''
  if (unitId.value) fetchLocations()
}, { immediate: true })

watch(debouncedSearchLocation, () => {
  fetchLocations()
})

// Selected location object (for display name)
const selectedLocationObj = ref(null)

watch(selectedLocation, val => {
  if (val && locationData.value) {
    const items = locationData.value?.data ?? locationData.value ?? []

    selectedLocationObj.value = items.find(l => l.id === val) ?? null
  } else {
    selectedLocationObj.value = null
  }
})

// Export
const isExporting = ref(false)

const exportData = async () => {
  if (!selectedLocation.value) return

  isExporting.value = true

  const url = createUrl('infection-control/device-day-counts/export', {
    query: {
      location_id: selectedLocation.value,
      month: selectedMonth.value,
      year: selectedYear.value,
    },
  })

  await $api(url.value).then(async () => {
    await new Promise(resolve => setTimeout(resolve, 2000))
  }).catch(error => {
    console.error('Export error:', error)
  })

  isExporting.value = false
}
</script>

<template>
  <section>
    <!-- Filter Card -->
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText>
        <!-- Row 1: Title block -->
        <div class="mb-4">
          <span
            class="text-overline text-primary font-weight-bold mb-1"
            style="line-height: 0px"
          >
            DAILY DENOMINATORS
          </span>
          <h5 class="text-h5 font-weight-semibold mt-1">
            Device-Day Entry
          </h5>
          <p class="text-body-2 text-medium-emphasis mt-1 mb-0">
            Record patient-days and device-days for each ward. These feed CLABSI, CAUTI & VAP rates.
          </p>
        </div>

        <!-- Row 2: Filters + Export -->
        <VRow align="end">
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <AppAutocomplete
              v-model="selectedLocation"
              v-model:search="searchLocation"
              :items="locationData ?? []"
              item-title="name"
              item-value="id"
              label="Location / Ward"
              placeholder="Search Location"
              :loading="fetchingLocations"
              clearable
              :return-object="false"
            />
          </VCol>

          <VCol
            cols="6"
            sm="3"
            md="2"
          >
            <AppSelect
              v-model="selectedMonth"
              :items="monthOptions"
              item-title="title"
              item-value="value"
              label="Month"
            />
          </VCol>

          <VCol
            cols="6"
            sm="3"
            md="2"
          >
            <AppSelect
              v-model="selectedYear"
              :items="yearOptions"
              item-title="title"
              item-value="value"
              label="Year"
            />
          </VCol>

          <VCol
            cols="12"
            sm="auto"
            class="ms-auto"
          >
            <VBtn
              variant="outlined"
              prepend-icon="tabler-download"
              :disabled="!selectedLocation"
              :loading="isExporting"
              @click="exportData"
            >
              Export CSV
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Entry Form (Today + Overview + History) -->
    <DenominatorEntryForm
      v-if="selectedLocation"
      :location-id="selectedLocation"
      :location-name="selectedLocationObj?.name ?? ''"
      :unit-id="unitId"
      :month="selectedMonth"
      :year="selectedYear"
    />

    <!-- No Location Selected -->
    <VCard
      v-if="!selectedLocation"
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-column align-center justify-center pa-8 text-medium-emphasis">
        <VIcon
          icon="tabler-map-pin"
          size="40"
          class="mb-3"
        />
        <span class="text-body-2">Select a location above to start entering device-day counts</span>
      </VCardText>
    </VCard>
  </section>
</template>
